//* Libraries imports
import { useState, useRef, useEffect } from "react";
import {
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Camera,
} from "lucide-react";
import { CircularProgress } from "@mui/material";

//* Componentes imports
import { Button } from "@components/ui/button";
import { Card, CardContent } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { Separator } from "@components/ui/separator";
import { TutorialSection } from "./TutorialSection";
import { ActivitySection } from "./ActivitySection";

//* Types imports
import { IProductPhase, IActivity } from "../types";
import { ApiError } from "@/types/api";
import { PhaseFormOutputDTO } from "@features/phaseForm/types";

type PhaseSectionProps = {
  phases: IProductPhase[];
  onPhaseUpdate: (index: number, phase: IProductPhase) => void;
  onPhaseRemove: (index: number) => void;
  onAddPhase: () => void;
  error: ApiError | null;
  onActivityUpdate: (
    phaseIndex: number,
    activityIndex: number,
    activity: IActivity
  ) => void;
  onActivityRemove: (phaseIndex: number, activityIndex: number) => void;
  onAddActivity: (phaseIndex: number) => void;
  availableForms: PhaseFormOutputDTO[];
  saveFile: any;
  documents: any;
  templates: any;
  techinicals: any;
  categories: any;
  formData: any;
  selectType: any;
  setTabSelect: (tab: number) => void;
  tabSelect: number;
  setAppointments: (appointments: any) => void;
  loadDocumentos: () => void;
};

export const PhaseSection = ({
  phases,
  onPhaseUpdate,
  onPhaseRemove,
  onAddPhase,
  error,
  onActivityUpdate,
  onActivityRemove,
  onAddActivity,
  availableForms,
  saveFile,
  loadDocumentos,
  categories,
  documents,
  templates,
  techinicals,
  formData,
  selectType,
  setTabSelect,
  tabSelect,
  setAppointments,
}: PhaseSectionProps) => {
  const [activePhaseIndex, setActivePhaseIndex] = useState<number>(0);
  const [loadingFileTutorial, setLoadingFileTutorial] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const selectTypeLast = useRef(selectType);
  const lastPhasesLength = useRef(phases.length);

  useEffect(() => {
    if (
      phases.filter((item) => item.type == selectType).length > 0 &&
      phases[activePhaseIndex] == undefined
    ) {
      let first_phase = phases.filter((item) => item.type == selectType);
      if (first_phase[0] == undefined) {
        setActivePhaseIndex(0);
      } else {
        let index = phases.findIndex((item) => item.type == selectType);
        console.log("index", index);
        setActivePhaseIndex(index);
      }
    }
  }, [phases, selectType]);

  useEffect(() => {
    if (
      selectType != selectTypeLast.current ||
      lastPhasesLength.current != phases.length
    ) {
      selectTypeLast.current = selectType;
      lastPhasesLength.current = phases.length;
      let first_phase = phases.filter((item) => item.type == selectType);
      if (first_phase[0] == undefined) {
        setActivePhaseIndex(0);
      } else {
        let index = phases.findIndex((item) => item.type == selectType);
        console.log("index", index);
        setActivePhaseIndex(index);
      }
    }
  }, [selectType, phases.length]);

  // Se não houver fases, não renderiza a seção
  if (phases.filter((item) => item.type == selectType).length === 0) {
    return (
      <section
        className="flex flex-col w-full items-start"
        style={{ background: "white" }}
      >
        <div className="flex w-full items-center mb-5">
          <div className="inline-flex items-center">
            <h1 className="text-2xl font-bold" id="fases">
              Fases (0)
            </h1>
          </div>

          <div className="flex-1"></div>

          <div className="inline-flex items-center gap-5">
            <Button
              size="icon"
              className="w-[30px] h-[30px] p-0 bg-blue-500 rounded-lg"
              onClick={onAddPhase}
            >
              <PlusIcon className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center p-10 w-full">
          <p
            className="text-clarotextos-02"
            style={{ textAlign: "center", color: "grey" }}
          >
            Nenhuma fase cadastrada. Clique no botão + para adicionar uma fase.
          </p>
        </div>
      </section>
    );
  }

  const activePhase = phases[activePhaseIndex];

  if (activePhase == undefined) {
    return <></>;
  }
  const totalPhases = phases.filter((phase) => phase.type == selectType).length;

  // Função para navegar entre as fases filtradas
  const handlePhaseNavigation = (direction: "prev" | "next") => {
    const filteredIndices = phases
      .map((p, i) => (p.type === selectType ? i : -1))
      .filter((i) => i !== -1);

    const currentFilteredIndex = filteredIndices.indexOf(activePhaseIndex);

    if (direction === "prev" && currentFilteredIndex > 0) {
      setActivePhaseIndex(filteredIndices[currentFilteredIndex - 1]);
    } else if (
      direction === "next" &&
      currentFilteredIndex < filteredIndices.length - 1
    ) {
      setActivePhaseIndex(filteredIndices[currentFilteredIndex + 1]);
    }
  };

  const filteredIndices = phases
    .map((p, i) => (p.type === selectType ? i : -1))
    .filter((i) => i !== -1);
  const currentFilteredIndex = filteredIndices.indexOf(activePhaseIndex);

  // Função para atualizar os campos da fase ativa
  const handleFieldChange = async (field: string, value: any) => {
    if (field == "image") {
      setLoadingFile(true);
      let response = await saveFile(value);
      const updatedPhase = { ...activePhase, [field]: response?.data?.path };
      setLoadingFile(false);
      onPhaseUpdate(activePhaseIndex, updatedPhase);
    } else {
      const updatedPhase = { ...activePhase, [field]: value };
      onPhaseUpdate(activePhaseIndex, updatedPhase);
    }
  };

  let index_phase = 0;

  return (
    <>
      <section className="flex flex-col w-full items-start">
        {/* Header */}
        <div className="flex w-full items-center mb-5">
          <div className="inline-flex items-center">
            <h2 className="text-2xl font-bold" id="fases">
              Fases ({totalPhases})
            </h2>
          </div>

          <div className="flex-1"></div>

          <div className="inline-flex items-center gap-5">
            <Button
              variant="outline"
              size="icon"
              disabled={loadingFile || loadingFileTutorial}
              className="w-[30px] h-[30px] p-0 bg-clarofrente-01 border-[#d8e0f0] rounded-lg"
              onClick={() => onPhaseRemove(activePhaseIndex)}
            >
              <img src="/small/excluir.svg" className="h-4 w-4" alt="Excluir" />
            </Button>

            <Button
              size="icon"
              disabled={loadingFile || loadingFileTutorial}
              className="w-[30px] h-[30px] p-0 bg-blue-500 rounded-lg"
              onClick={onAddPhase}
            >
              <PlusIcon className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>
        {error?.validationErrors?.[`phases`] && (
          <p className="text-red-500 text-sm px-1.5 mb-3">
            {error.validationErrors[`phases`].message}
          </p>
        )}
        <Separator className="w-full my-1" />

        {/* Pagination */}
        <div className="flex w-full items-center justify-center rounded-xl mb-5">
          <div className="flex items-center justify-center gap-5 w-full">
            <div className="flex items-center justify-center gap-2.5 flex-1 rounded-xl">
              <Button
                variant="ghost"
                disabled={
                  loadingFile ||
                  loadingFileTutorial ||
                  currentFilteredIndex <= 0
                }
                size="icon"
                className="p-0 h-[25px]"
                onClick={() => handlePhaseNavigation("prev")}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>

              <div className="flex items-center justify-start gap-2.5 flex-1">
                {phases.map((phase, idx) => {
                  if (phase.type == selectType) {
                    index_phase += 1;
                  }
                  return (
                    phase.type == selectType && (
                      <Button
                        disabled={loadingFile || loadingFileTutorial}
                        key={phase._id || `phase-${idx}`}
                        variant="outline"
                        className={`w-[30px] h-[25px] p-1.5 rounded ${
                          idx === activePhaseIndex
                            ? "bg-blue-500 text-white"
                            : "bg-clarofrente-01 text-clarotextos-01"
                        } border-[0.5px] border-[#d8e0f0]`}
                        onClick={() => setActivePhaseIndex(idx)}
                      >
                        <span className="font-inter-14-medium text-center">
                          {String(index_phase).padStart(2, "0")}
                        </span>
                      </Button>
                    )
                  );
                })}
              </div>

              <Button
                variant="ghost"
                size="icon"
                disabled={
                  loadingFile ||
                  loadingFileTutorial ||
                  currentFilteredIndex >= filteredIndices.length - 1
                }
                className="p-0 h-[25px]"
                onClick={() => handlePhaseNavigation("next")}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <Separator className="w-full my-1" />

        {/* Phase Content */}
        <Card className="w-full border-0 shadow-none p-0">
          <CardContent className="p-0 space-y-4">
            {/* Phase Header */}
            <div className="flex w-full items-start">
              <div className="flex items-center gap-[18px] flex-1">
                <div>
                  <div style={{ position: "relative", display: "flex" }}>
                    <input
                      type="file"
                      accept="image/*"
                      id={"image_phase" + activePhaseIndex}
                      name={"image"}
                      onChange={(e: any) =>
                        handleFieldChange("image", e.target.files[0])
                      }
                      hidden
                    />
                    <div
                      onClick={() =>
                        document
                          .getElementById("image_phase" + activePhaseIndex)
                          ?.click()
                      }
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        minWidth: 100,
                        minHeight: 100,
                        width: 100,
                        height: 100,
                        borderRadius: 10,
                        border: "1px solid lightgrey",
                        padding: 5,
                        overflow: "hidden",
                      }}
                    >
                      {activePhase.image && (
                        <img
                          style={{
                            borderRadius: 10,
                            minWidth: 90,
                            minHeight: 90,
                            objectFit: "cover",
                          }}
                          src={
                            activePhase.image
                              ? activePhase.image instanceof File
                                ? URL.createObjectURL(activePhase.image)
                                : activePhase.image
                              : "/agenda.svg"
                          }
                          alt="Phase"
                        />
                      )}
                      {!activePhase.image && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={70}
                          style={{ paddingLeft: 5 }}
                          height="70"
                          viewBox="0 0 20 16"
                          fill="none"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M21.0725 0.60016V3.79598H9.26844V0.721711C9.26844 0.654605 9.32297 0.600098 9.39004 0.600098L21.0725 0.60016Z"
                            fill="#1777CF"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M18.8804 4.3012V7.49696H7.07638V4.42257C7.07638 4.35559 7.13091 4.30114 7.19798 4.30114L18.8804 4.3012Z"
                            fill="#1777CF"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.6883 8.00231V11.1979H4.88425V8.12374C4.88425 8.05682 4.93878 8.00231 5.00585 8.00231H16.6883Z"
                            fill="#1777CF"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.4962 11.7032V14.899H2.69213V11.8248C2.69213 11.7579 2.74659 11.7032 2.81373 11.7032L14.4962 11.7032Z"
                            fill="#1777CF"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12.3041 15.4043V18.6001H0.5V15.5259C0.5 15.459 0.55453 15.4043 0.621601 15.4043H12.3041V15.4043Z"
                            fill="#1777CF"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M23.5 0.60016V18.6001H12.8096V15.4043H14.749C14.8886 15.4043 15.0017 15.2913 15.0017 15.1517V11.7033H16.9411C17.0807 11.7033 17.1938 11.5902 17.1938 11.4507V8.00231H19.1332C19.2729 8.00231 19.386 7.88912 19.386 7.74967V4.30127H21.3253C21.465 4.30127 21.5781 4.18807 21.5781 4.04862V0.60016H23.5Z"
                            fill="#1777CF"
                          />
                        </svg>
                      )}
                    </div>
                    <div
                      onClick={() =>
                        document
                          .getElementById("image_phase" + activePhaseIndex)
                          ?.click()
                      }
                      style={{
                        cursor: "pointer",
                        boxShadow: "rgba(0, 0, 0, 0.2) -1px 2px 1px 0px",
                        padding: 2,
                        position: "absolute",
                        top: 80,
                        left: 70,
                        background: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                      }}
                    >
                      <Camera width={18} height={18} />
                    </div>
                    <div style={{ padding: 10, paddingTop: 20 }}>
                      <p style={{ fontSize: 14, color: "grey" }}>
                        Fase: {String(activePhaseIndex + 1).padStart(2, "0")}
                      </p>
                      <p style={{ fontSize: 16 }}>
                        <b>{activePhase?.name}</b>
                      </p>
                      {loadingFile && <CircularProgress size={18} />}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="w-full my-2.5" />

            {/* Form Fields */}
            <div className="w-full space-y-4">
              {/* Phase Number */}
              <div className="flex flex-col items-start gap-1.5 w-full">
                <label className="text-sm font-semibold text-gray-700">
                  N° da Fase <b style={{ color: "red" }}>*</b>
                </label>
                <Input
                  className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    error?.validationErrors?.[
                      `phases.${activePhaseIndex}.order`
                    ]
                      ? "border-red-500"
                      : ""
                  }`}
                  value={String(activePhaseIndex + 1).padStart(2, "0")}
                  disabled
                />
                {error?.validationErrors?.[
                  `phases.${activePhaseIndex}.order`
                ] && (
                  <p className="text-red-500 text-sm px-1.5">
                    {
                      error.validationErrors[`phases.${activePhaseIndex}.order`]
                        .message
                    }
                  </p>
                )}
              </div>

              {/* Title */}
              <div className="flex flex-col items-start gap-1.5 w-full">
                <label className="text-sm font-semibold text-gray-700">
                  Título <b style={{ color: "red" }}>*</b>
                </label>
                <Input
                  className={`h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    error?.validationErrors?.[`phases.${activePhaseIndex}.name`]
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Digite o nome da fase"
                  value={activePhase.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                />
                {error?.validationErrors?.[
                  `phases.${activePhaseIndex}.name`
                ] && (
                  <p className="text-red-500 text-sm px-1.5">
                    {
                      error.validationErrors[`phases.${activePhaseIndex}.name`]
                        .message
                    }
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col items-start gap-1.5 w-full">
                <label className="text-sm font-semibold text-gray-700">
                  Descrição
                </label>
                <Textarea
                  className={`min-h-[120px] rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    error?.validationErrors?.[
                      `phases.${activePhaseIndex}.description`
                    ]
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Digite aqui..."
                  value={activePhase.description || ""}
                  onChange={(e) =>
                    handleFieldChange("description", e.target.value)
                  }
                />
                {error?.validationErrors?.[
                  `phases.${activePhaseIndex}.description`
                ] && (
                  <p className="text-red-500 text-sm px-1.5">
                    {
                      error.validationErrors[
                        `phases.${activePhaseIndex}.description`
                      ].message
                    }
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-4">
                <TutorialSection
                  id={`phase-tutorial-${activePhaseIndex}`}
                  tutorial={activePhase.tutorial || {}}
                  onChange={(newTutorial) =>
                    handleFieldChange("tutorial", newTutorial)
                  }
                  onUpload={saveFile}
                  loadingFile={loadingFile}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
      {phases.length > 0 && (
        <>
          <Separator className="w-full my-5" />
          <ActivitySection
            phases={phases}
            categories={categories}
            loadDocumentos={loadDocumentos}
            documents={documents}
            templates={templates}
            techinicals={techinicals}
            activePhaseIndex={activePhaseIndex}
            onActivityUpdate={onActivityUpdate}
            onActivityRemove={onActivityRemove}
            onAddActivity={onAddActivity}
            error={error}
            availableForms={availableForms}
            saveFile={saveFile}
            formData={formData}
            setTabSelect={setTabSelect}
            tabSelect={tabSelect}
            setAppointments={setAppointments}
          />
        </>
      )}
    </>
  );
};
